import React from "react";
import { ExlyDropdown, ExlyInput } from "common/form";
import { required } from "common/validate";
import { Field, useForm, useFormState } from "react-final-form";
import { isMultiQuestionType } from "ui/pages/QuestionsPage/utils/QuestionsPage.utils";
import {
  QUESTION_FORM_KEYS,
  QUESTION_TYPE_IDS,
  questionOptions,
  renderTypeSpecificValidation,
} from "./constants/QuestionFormFields.constants";
import { getFormValueByPath } from "features/Common/modules/Form/Form.utils";
import {
  KYC_QUESTIONS_COLUMNS_CONFIG,
  QUESTION_TYPE_ID_FEATURE_KEY_MAPPING,
} from "constants/customerQuestions.constants";
import { FormFieldLabel } from "features/Common/modules/Form/modules/FormFieldLabel/FormFieldLabel";
import { Tooltip } from "@my-scoot/component-library-legacy";
import { InfoOutlined } from "@material-ui/icons";
import ExlySwitch from "common/form/ExlySwitch";
import withComponentLibraryTheme from "hocs/withComponentLibraryTheme";
import {
  generateQuestionTypeOptions,
  getMultiQuestionsWithDisabledState,
} from "./utils/QuestionFormFields.utils";
import { NoteChip } from "../NoteChip/NoteChip";
import { useQuestionFields } from "./hooks/useQuestionFields";
import { CheckCreditExpiry } from "common/Components/UBP/CheckCreditExpiry";
import { MultiCheckBoxInputTable } from "../MultiCheckBoxInputTable/MultiCheckBoxInputTable";
import { question_placement_types } from "schedule-v2/commonPages/Components/QuestionCards/questionFormConstants";

export const QuestionFormFields = withComponentLibraryTheme(
  ({ showQuestionPlacementField = true }) => {
    const formState = useFormState();
    const form = useForm();

    const { handleFieldsChange, handleTypeChange } = useQuestionFields({
      form,
    });

    const questionObj = formState?.values?.questionObj;
    const isDefaultQuestion = formState?.values?.questionObj?.is_default;
    const questionType = formState?.values?.questionObj?.type;
    const areChoicesNeeded = [
      QUESTION_TYPE_IDS.MULTI_SELECT_DROPDOWN,
      QUESTION_TYPE_IDS.SINGLE_SELECT_DROPDOWN,
    ].includes(questionType);
    // If Multi Type Question then we will not allow user to edit these custom Questions
    const isQuestionMultiType = isMultiQuestionType(questionType);
    const showCreditExpiryAlert =
      isQuestionMultiType &&
      QUESTION_TYPE_ID_FEATURE_KEY_MAPPING?.[questionType];

    const multiQuestionsList = getMultiQuestionsWithDisabledState(
      getFormValueByPath(
        formState?.values,
        QUESTION_FORM_KEYS.multi_questions.key
      ) ?? []
    );
    const showQuestionPlacementInputField =
      showQuestionPlacementField && !isDefaultQuestion;

    return (
      <>
        {showCreditExpiryAlert && (
          <CheckCreditExpiry
            feature_key={QUESTION_TYPE_ID_FEATURE_KEY_MAPPING[questionType]}
            warningClosable={false}
          />
        )}
        <div className={"tw-flex tw-flex-col tw-gap-[14px] tw-p-[18px]"}>
          <Field
            name={QUESTION_FORM_KEYS.question.key}
            placeholder={QUESTION_FORM_KEYS.question.placeholder}
            label={
              <FormFieldLabel
                formKeys={QUESTION_FORM_KEYS}
                formKey={QUESTION_FORM_KEYS.question.labelKey}
                classes={{ spanClassName: "tw-mr-1" }}
                required
              />
            }
            size="small"
            component={ExlyInput}
            validate={required}
            fullWidth
          />

          <div>
            <Field
              name={QUESTION_FORM_KEYS.type.key}
              label={
                <FormFieldLabel
                  formKeys={QUESTION_FORM_KEYS}
                  formKey={QUESTION_FORM_KEYS.type.labelKey}
                  classes={{ spanClassName: "tw-mr-1" }}
                  required
                />
              }
              size="small"
              component={ExlyDropdown}
              options={generateQuestionTypeOptions({
                questionType,
                isQuestionMultiType,
              })}
              validate={required}
              disabled={isDefaultQuestion}
              fullWidth
              onChange={handleTypeChange}
            />
            {questionType === QUESTION_TYPE_IDS.EMAIL && (
              <NoteChip text="Customers can enter any email address." />
            )}
            {questionType === QUESTION_TYPE_IDS.PHONE_NUMBER && (
              <NoteChip text="Customers can select a country code and enter their phone no." />
            )}
            {areChoicesNeeded && (
              <div className="mt-2">
                {renderTypeSpecificValidation(formState?.values?.questionObj)}
              </div>
            )}
          </div>

          {isMultiQuestionType(questionType) && (
            <Field
              name={QUESTION_FORM_KEYS.multi_questions.key}
              placeholder={QUESTION_FORM_KEYS.question.placeholder}
              label={
                <FormFieldLabel
                  formKeys={QUESTION_FORM_KEYS}
                  formKey={QUESTION_FORM_KEYS.question.labelKey}
                  classes={{ spanClassName: "tw-mr-1" }}
                  required
                />
              }
              size="small"
              component={MultiCheckBoxInputTable}
              validate={required}
              fullWidth
              multiQuestions={multiQuestionsList}
              columns={KYC_QUESTIONS_COLUMNS_CONFIG}
              onChange={(value, updatedRow, updatedSubKey) =>
                handleFieldsChange({
                  key: QUESTION_FORM_KEYS.multi_questions.key,
                  value,
                  updatedSubKey,
                  question: questionObj,
                })
              }
            />
          )}

          {showQuestionPlacementInputField && (
            <Field
              name={QUESTION_FORM_KEYS.question_placement.key}
              label={
                <FormFieldLabel
                  formKeys={QUESTION_FORM_KEYS}
                  formKey={QUESTION_FORM_KEYS.question_placement.labelKey}
                  classes={{ spanClassName: "tw-mr-1" }}
                  required
                />
              }
              fullWidth="100%"
              options={question_placement_types}
              size="small"
              style={{ width: "100%" }}
              component={ExlyDropdown}
            />
          )}

          <div className="tw-flex tw-flex-col tw-gap-2">
            {questionOptions.map(
              ({
                label,
                formKey,
                supportedQuestionTypes,
                disabled = null,
                tooltipJSX,
              }) => {
                if (!supportedQuestionTypes.includes(questionType)) return;
                const isSwitchDisabled =
                  disabled == false ? false : isDefaultQuestion;

                return (
                  <div
                    key={formKey}
                    className="tw-flex tw-items-center tw-justify-between"
                  >
                    <div className="tw-flex tw-items-center">
                      <div className="tw-text-[14px] tw-font-medium tw-text-black/85 tw-tracking-[0.28px]">
                        {label}
                      </div>

                      <Tooltip title={tooltipJSX} color="primary">
                        <InfoOutlined
                          fontSize="small"
                          className="tw-w-[15px] tw-h-[15px] tw-text-[#27252280] tw-ml-1 tw-mt-[2px]"
                        />
                      </Tooltip>
                    </div>

                    <Field
                      component={ExlySwitch}
                      name={formKey}
                      noIcon
                      size="large"
                      color="success"
                      input={{
                        value:
                          getFormValueByPath(formState?.values, formKey) ??
                          false,
                        onChange: (value) => {
                          if (isSwitchDisabled) return;
                          handleFieldsChange({
                            key: formKey,
                            value: value,
                            question: questionObj,
                          });
                        },
                      }}
                      rootWrapperClassName={
                        isSwitchDisabled
                          ? "tw-opacity-50 tw-cursor-not-allowed tw-pointer-events-none"
                          : ""
                      }
                    />
                  </div>
                );
              }
            )}
          </div>
        </div>
      </>
    );
  }
);
